import { Children, ComponentType, ReactElement, ReactNode, isValidElement } from 'react';
import Box from '@mui/material/Box';
import { Alert } from 'framework/ui/mui-builder';
import LeavesRowGrid from './LeavesRowGrid';
import LeavesGridHeader from './LeavesGridHeader';
import { ProcessedLeaveYear } from '../../shared/types';
import { ListFieldLayout } from 'framework/ui/layout';
import { ListLoadingDecorator, RowGridProps, useListContext } from 'framework/list';
import { UiThemeOverrider } from 'framework/uiTheme';
import { Data } from 'framework/types';

interface LeavesDataGridLayoutProps {
  children: ReactNode;
  rowComponent?: ComponentType<RowGridProps>;
  rowKeySource?: string;
  customEmptyMessage?: string;
  expandable?: boolean;
  isExpandableItem?: (data: Data<any>) => boolean;
  expandableSection?: ReactNode;
  defaultExpandedItems?: (data: ProcessedLeaveYear) => boolean;
}

const LeavesDataGridLayout = ({
  children,
  rowComponent: RowComponent = LeavesRowGrid,
  rowKeySource = 'id',
  customEmptyMessage = 'No records found',
  expandable = false,
  isExpandableItem,
  expandableSection,
  defaultExpandedItems,
}: LeavesDataGridLayoutProps) => {
  const { data: { items = [] } = {}, loading } = useListContext();
  const hasItems = items.length > 0;
  const templateChildren = Children.toArray(children).filter((child): child is ReactElement => isValidElement(child));

  return (
    <ListLoadingDecorator items={items} loading={loading} hasPositionFixed isInitialPosition>
      <UiThemeOverrider overriddenLayouts={{ BaseFieldLayout: ListFieldLayout }}>
        <Box maxWidth="100%" overflow="auto">
          <Box
            sx={{
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              minWidth: '1000px',
              '& .balances-leaves-column': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            <LeavesGridHeader expandable={expandable}>{templateChildren}</LeavesGridHeader>

            {hasItems ? (
              items.map((item, index) => (
                <RowComponent
                  key={`row-${item?.[rowKeySource] ?? index}`}
                  data={item}
                  template={templateChildren}
                  rowKeySource={rowKeySource}
                  expandable={expandable}
                  isExpandableItem={isExpandableItem}
                  expandableSection={expandableSection}
                  expanded={defaultExpandedItems ? defaultExpandedItems(item) : false}
                />
              ))
            ) : (
              <Alert color="info" sx={{ mt: 1.5 }}>
                {customEmptyMessage}
              </Alert>
            )}
          </Box>
        </Box>
      </UiThemeOverrider>
    </ListLoadingDecorator>
  );
};

export default LeavesDataGridLayout;
